import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import * as Yup from "yup";
import { passwordSchema, confirmPasswordSchema, passwordRequirements } from "../../common/passwordPolicy";
import { FieldTypes } from "../Forms/FieldTypes";
import { FormField } from "../Forms/FormField";
import Form from "../Forms/Form";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios, { AxiosError } from "axios";
import Loading from "../Widgets/Loading";
import InvitationUnavailable from "./InvitationUnavailable";
import UnauthenticatedPageLayout from "../shared/UnauthenticatedPageLayout";
import SessionService from "../../services/SessionService";
import { useCerberus } from "../../hooks/useCerberus";
import { useSnackbar } from "notistack";
import apiClient from "../../api/apiClient";

function getFields(invitation: { email: string; name: string }) {
  const fields: FormField[] = [
    {
      id: "email",
      text: "Email",
      disabled: true,
      value: invitation.email,
      type: FieldTypes.Text,
      yupSchema: Yup.string()
        .required("Required")
        .email("Must be a valid email address")
        .max(254, "Email address is too long"),
    },
    {
      id: "name",
      text: "Name",
      disabled: true,
      value: invitation.name,
      type: FieldTypes.Text,
      yupSchema: Yup.string()
        .required("Required")
        .max(100, "Must be 100 characters or less"),
    },
    {
      id: "password",
      text: "New Password",
      password: true,
      type: FieldTypes.Text,
      yupSchema: passwordSchema(),
    },
    {
      id: "confirmPassword",
      text: "Confirm New Password",
      password: true,
      type: FieldTypes.Text,
      yupSchema: confirmPasswordSchema("password"),
    },
  ];

  return fields;
}

const AcceptInvitation = () => {
  const { invitationId } = useParams();
  const [invitation, setInvitation] = useState({ email: "", name: "" });
  const [loading, setLoading] = useState(true);
  // Two different failures used to share one state variable, and the render treated both as
  // terminal by replacing the whole form with a line of text:
  //   loadError   the invitation itself is bad (missing/expired) -> nothing to retry, no form
  //   submitError this attempt was rejected (e.g. Cognito refused the password) -> the invitation
  //               is still fine, so keep the form up and let them correct it
  // Collapsing them meant a rejected password left the user staring at an error with no way back.
  const [loadError, setLoadError] = useState<string>();
  const [submitError, setSubmitError] = useState<string>();
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaSession, setMfaSession] = useState("");
  const [mfaError, setMfaError] = useState<string>();
  const [mfaCode, setMfaCode] = useState("");
  const sessionService = SessionService.getInstance();
  const { refreshUserData } = useCerberus();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit = async (values: Record<string, unknown>) => {
    setSubmitError(undefined);
    const newUser = {
      ...values,
    };

    try {
      // apiClient, not bare axios. It carries `withCredentials`, which is what actually signs the
      // user in: the server returns the session as an httpOnly cookie, and without it the browser
      // silently drops the Set-Cookie (the API is a different origin). The account was created
      // either way, so the client believed it had logged in, recorded a session expiry, and
      // redirected — to a blank page with no session behind it. It also carries the
      // `application-name` header the server now requires on every mutating request (AUD-9), which
      // this call spelled out by hand and its sibling screen had been missing entirely.
      const result = await apiClient.post(
        `/invitation/${invitationId}/accept`,
        newUser,
        // The form renders the failure itself, inline and recoverable (see submitError below), so
        // the global dialog would be a second report of the same thing.
        { suppressErrorDialog: true },
      );

      if (result.status === 200) {
        // Check for success/failure
        if (result.data.success === false) {
          setSubmitError(result.data.error);
        } else {
          // Check if MFA is required
          if (result.data.requiresMfa) {
            setMfaRequired(true);
            setMfaSession(result.data.mfaSession);
            enqueueSnackbar("Please enter the verification code sent to your email", {
              variant: "info",
            });
          } else if (result.data.success) {
            // Auto-login: the server set the session cookie on this response;
            // we just record the expiry locally for countdowns.
            sessionService.setSession(result.data.expiresIn);
            await refreshUserData();
            enqueueSnackbar("Account created successfully! You are now logged in.", {
              variant: "success",
            });
            // Client-side, in the same commit as the identity update. A full page load
            // (window.location) left the app signed in at /i/<id> for the length of the
            // reload — a route the authenticated tree does not have, so the user watched
            // the 404 page until the new document arrived. `replace` also drops the spent
            // invitation from history.
            navigate("/", { replace: true });
          } else {
            // This case should ideally not be reached if the invitation is accepted successfully
            // but as a fallback, we can set a message or redirect.
            setSubmitError("Unexpected response from server.");
          }
        }
      } else {
        // The page already renders submitError inline; a native alert() blocks the whole tab and
        // looks nothing like the rest of the product.
        setSubmitError("Something went wrong. Please try again later.");
      }
    } catch {
      setSubmitError("An error occurred while accepting the invitation. Please try again.");
    }
  };

  const handleMfaSubmit = async (values: Record<string, unknown>) => {
    try {
      // Same as the accept call: the session comes back as an httpOnly cookie, which is dropped
      // without apiClient's `withCredentials`.
      const response = await apiClient.post(
        `/auth/verify-mfa`,
        {
          username: invitation.email,
          code: values.code,
          session: mfaSession,
        },
        // A wrong code is a 400, and this screen shows it inline under the field (mfaError). Without
        // this the user would get the message twice, once in a modal they have to dismiss before
        // they can retype the code.
        { suppressErrorDialog: true },
      );

      if (response.data.success) {
        sessionService.setSession(response.data.expiresIn);
        await refreshUserData();
        enqueueSnackbar("Account created successfully! You are now logged in.", {
          variant: "success",
        });
        // Same reasoning as the accept path above: client-side, replacing the invite URL.
        navigate("/", { replace: true });
      } else {
        setMfaError(response.data.message || "Verification failed");
      }
    } catch (error) {
      let message = "Verification failed";
      if (axios.isAxiosError(error)) {
        message = error.response?.data?.message || message;
      }
      setMfaError(message);
    }
  };

  const renderContentSection = () => {
    // Terminal: the invitation itself is unusable, so there is nothing to retry. Say what happened
    // and give them somewhere to go, rather than leaving them on a dead page.
    if (loadError) {
      return <InvitationUnavailable />;
    } else if (mfaRequired) {
      // Hand-built rather than the shared declarative Form: that renders a full-width field with the
      // submit button pushed to the right, which is the wrong shape for a six-character code. A
      // one-time code wants to be short, centered, autofocused, and offered to the OS autofill
      // (autocomplete="one-time-code" is what lets iOS/macOS surface the code from the email).
      return (
        <Box
          component="form"
          onSubmit={(event: React.FormEvent) => {
            event.preventDefault();
            if (mfaCode.length === 6) handleMfaSubmit({ code: mfaCode });
          }}
          sx={{ textAlign: "center" }}
        >
          <Typography
            component="h1"
            variant="h5"
            sx={{ fontWeight: 700, color: "primary.dark", mb: 1 }}
          >
            Verify your email
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
            We sent a 6-digit code to{" "}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {invitation.email}
            </Box>
            . Enter it below to finish creating your account.
          </Typography>

          {mfaError && (
            <Alert severity="error" sx={{ mb: 3, textAlign: "left" }}>
              {mfaError}
            </Alert>
          )}

          <TextField
            // The rule is right in general and wrong here: this screen exists to collect one code and
            // has no other control, so focusing it is what a screen-reader user wants too — the
            // alternative is making everyone hunt for the only field on the page.
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            id="code"
            name="code"
            label="Verification code"
            value={mfaCode}
            onChange={(event) =>
              // Digits only, capped at six — so a pasted "123 456" or a stray letter can't produce a
              // code that only fails once the server has rejected it.
              setMfaCode(event.target.value.replace(/\D/g, "").slice(0, 6))
            }
            error={Boolean(mfaError)}
            slotProps={{
              htmlInput: {
                inputMode: "numeric",
                autoComplete: "one-time-code",
                maxLength: 6,
                style: {
                  textAlign: "center",
                  fontSize: "1.75rem",
                  letterSpacing: "0.5rem",
                  paddingLeft: "0.5rem",
                },
              },
            }}
            sx={{ width: 240, mb: 3 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={mfaCode.length !== 6}
          >
            Verify
          </Button>
        </Box>
      );
    } else {
      return (
        <>
          <Typography component="h1" variant="h4" align="center" sx={{ mb: 2 }}>
            Accept Invitation
          </Typography>

          {/* Recoverable: the form STAYS MOUNTED so they can correct the password and resubmit.
              This used to replace the whole form with a bare line of text — a dead end with no way
              back, which is what a rejected password looked like. */}
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}

          {/* State the rules up front rather than after a rejection. */}
          <Alert severity="info" icon={false} sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Your password must contain:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {passwordRequirements.map((requirement) => (
                <Typography component="li" variant="body2" key={requirement}>
                  {requirement}
                </Typography>
              ))}
            </Box>
          </Alert>

          <Form
            onSubmit={handleSubmit}
            fields={getFields(invitation)}
            fieldVariant="standard"
            submitButtonText="Submit"
          />
        </>
      );
    }
  };

  useEffect(() => {
    const fetchInvitation = async () => {
      try {
        // The read goes through apiClient too — not because AUD-9 requires it (it exempts safe
        // methods) but because there is no reason left for this screen to hold its own transport.
        // The failure is terminal and rendered inline, so no dialog.
        const result = await apiClient.get(`/invitation/${invitationId}`, {
          suppressErrorDialog: true,
        });

        setInvitation(result.data);
      } catch (error) {
        const axiosError = error as AxiosError;
        setLoadError((axiosError.response?.data as string) || "This invitation could not be found.");
      }

      setLoading(false);
    };

    fetchInvitation();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <Loading />;
  }

  return (
    <UnauthenticatedPageLayout>
      {/* maxWidth="sm", not "xl". The card used to span the full viewport, which is why a six-digit
          code sat on a 2000px-wide underline with the button marooned off to the right. Same
          logo-over-a-raised-card treatment as Login and the registration page, so the three
          consecutive screens of onboarding finally look like one product. */}
      {/* No logo here: UnauthenticatedPageLayout already puts one in the app bar, and two of the same
          mark stacked on one screen is just noise. */}
      <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 6 } }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 4 },
            borderRadius: 2,
            boxShadow: (theme) => theme.palette.brand.shadow.raised,
          }}
        >
          {renderContentSection()}
        </Paper>
      </Container>
    </UnauthenticatedPageLayout>
  );
};

export default AcceptInvitation;
